import * as React from "react";
import styles from "./FiltersSidebar.module.scss";
import { Accordion } from "../../../bits/Accordion/Accordion";
import { Button } from "../../../bits/Button/Button";
import {
    AgeCategory,
    ClinicianCredential,
    ClinicianSearchCredential,
    IExperienceLevelOption,
    ISearchParams,
    TimeCategory,
    DayOption,
} from "intake_shared/src/types";
import { defaultOffices } from "../../../../../store/cache/location";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { DepartmentCache } from "../../../../../store/cache/requested_service";
import store from "../../../../../store";
import { FormCheckbox } from "../../../bits/FormCheckbox/FormCheckbox";
import { cloneDeep } from "lodash";

const getCredentialGroup = (credential: string): string[] => {
    return Object.values(ClinicianCredential)
        .filter(
            (value) => value === credential || value.startsWith(`${credential}-`)
        )
        .map((value) => value.toLowerCase());
};

interface FiltersSidebarProps {
    searchParams: ISearchParams;
    setSearchParams: React.Dispatch<React.SetStateAction<ISearchParams>>;
    filtersCount: number;
    defaultSearchParams: ISearchParams;
    setTimePeriodFilters: React.Dispatch<React.SetStateAction<TimeCategory[]>>;
    setdayPeriodFilters: React.Dispatch<React.SetStateAction<DayOption[]>>;
    setSearchInputValue: React.Dispatch<React.SetStateAction<string>>;
}

export default function FiltersSidebar({
    searchParams,
    setSearchParams,
    filtersCount,
    defaultSearchParams,
    setTimePeriodFilters,
    setdayPeriodFilters,
    setSearchInputValue,
}: FiltersSidebarProps) {
    const [allExperienceLevelOptions, setAllExperienceLevelOptions] = React.useState<
        IExperienceLevelOption[]
    >([]);
    const [methodologyOptions, setMethodologyOptions] = React.useState<string[]>([]);
    const { offices: availableOffices = defaultOffices } = React.useContext(
        CacheContext
    ) || {
        offices: defaultOffices,
    };
    const olc = React.useContext(CacheContext);
    const allDepartmentsAndServices =
        olc?.allDepartmentAndServices || ({} as DepartmentCache);

    React.useEffect(() => {
        const fetchExperienceLevels = async () => {
            try {
                const levels = await store.getAllExperienceLevels();
                setAllExperienceLevelOptions(levels);
            } catch (ex) {
                console.error(ex);
            }
        };
        fetchExperienceLevels();

        store
            .getAllMethodologyOptions()
            .then((options) => setMethodologyOptions([...options].sort()))
            .catch(() => {});
    }, []);

    const updateSearchParams = (label: keyof ISearchParams, value: any) => {
        setSearchParams({ ...searchParams, [label]: value });
    };

    const groupExperienceLevels = () => {
        const experienceCategories: { [key: string]: JSX.Element[] } = {};
        allExperienceLevelOptions
            .filter((e) => e.show)
            .sort((a, b) => a.value.localeCompare(b.value))
            .forEach((experience, idx) => {
                const experienceElement = (
                    <FormCheckbox
                        label={experience.value}
                        checked={Boolean(
                            searchParams.experience_level[experience.value]
                        )}
                        onChange={() => {
                            updateSearchParams("experience_level", {
                                ...searchParams.experience_level,
                                [experience.value]: !Boolean(
                                    searchParams.experience_level[experience.value]
                                ),
                            });
                        }}
                        noBorder
                        labelClass={styles.filterAccordionLabel}
                    />
                );
                if (experienceCategories[experience.category]) {
                    experienceCategories[experience.category].push(
                        experienceElement
                    );
                } else {
                    experienceCategories[experience.category] = [experienceElement];
                }
            });
        return experienceCategories;
    };

    const groupedLevels = groupExperienceLevels();

    const handleCredentialChange = (credential: string) => {
        const updatedSearchCreds = [...searchParams.search_credentials];
        const credentialGroup = getCredentialGroup(credential);

        if (credentialGroup.length > 1) {
            if (credentialGroup.every((sub) => updatedSearchCreds.includes(sub))) {
                setSearchParams({
                    ...searchParams,
                    search_credentials: updatedSearchCreds.filter(
                        (cred) => !credentialGroup.includes(cred)
                    ),
                });
            } else {
                setSearchParams({
                    ...searchParams,
                    search_credentials: [
                        ...updatedSearchCreds,
                        ...credentialGroup.filter(
                            (sub) => !updatedSearchCreds.includes(sub)
                        ),
                    ],
                });
            }
        } else {
            const lower_cred = credential.toLowerCase();
            if (updatedSearchCreds.includes(lower_cred)) {
                setSearchParams({
                    ...searchParams,
                    search_credentials: updatedSearchCreds.filter(
                        (cred) => cred !== lower_cred
                    ),
                });
            } else {
                setSearchParams({
                    ...searchParams,
                    search_credentials: [...updatedSearchCreds, lower_cred],
                });
            }
        }
    };

    return (
        <div className={styles.filtersWrapper}>
            <div className={styles.filtersHeader}>
                <h3 className={styles.filtersTitle}>Filters</h3>
                <Button
                    disabled={filtersCount < 1}
                    onClick={() => {
                        setSearchParams(cloneDeep(defaultSearchParams));
                        setTimePeriodFilters([]);
                        setdayPeriodFilters([]);
                        setSearchInputValue("");
                    }}
                    variant="secondary"
                    size="small"
                >
                    {`Clear filters (${filtersCount})`}
                </Button>
            </div>
            <div className={styles.filtersContent}>
                <Accordion
                    title="Location"
                    variant="bordered"
                    className={styles.filterAccordion}
                    titleClassName={styles.filterAccordionTitle}
                    contentClassName={styles.filterAccordionContent}
                    defaultOpen={true}
                >
                    {Object.keys(availableOffices).map((k: string, idx) => (
                        <FormCheckbox
                            label={k}
                            checked={searchParams.search_locations.includes(
                                k.toLowerCase()
                            )}
                            onChange={() => {
                                const lower_location = k.toLowerCase();
                                const updatedSearchLocs =
                                    searchParams.search_locations;
                                if (updatedSearchLocs.includes(lower_location)) {
                                    setSearchParams({
                                        ...searchParams,
                                        search_locations: updatedSearchLocs.filter(
                                            (l) => l !== lower_location
                                        ),
                                    });
                                } else {
                                    setSearchParams({
                                        ...searchParams,
                                        search_locations: [
                                            ...updatedSearchLocs,
                                            lower_location,
                                        ],
                                    });
                                }
                            }}
                            noBorder
                            key={`check-location-${k}-${idx}`}
                            labelClass={styles.filterAccordionLabel}
                        />
                    ))}
                </Accordion>

                {/* Credential Accordion */}
                <Accordion
                    title="Credential"
                    variant="bordered"
                    className={styles.filterAccordion}
                    titleClassName={styles.filterAccordionTitle}
                    contentClassName={styles.filterAccordionContent}
                    defaultOpen={true}
                >
                    {Object.values(ClinicianSearchCredential).map(
                        (k: string, idx) => (
                            <FormCheckbox
                                label={k}
                                checked={searchParams.search_credentials.includes(
                                    k.toLowerCase()
                                )}
                                onChange={() => handleCredentialChange(k)}
                                noBorder
                                key={`check-credential-${k}-${idx}`}
                                labelClass={styles.filterAccordionLabel}
                            />
                        )
                    )}
                </Accordion>

                {/* Department Accordion */}
                <Accordion
                    title="Department"
                    variant="bordered"
                    className={styles.filterAccordion}
                    titleClassName={styles.filterAccordionTitle}
                    contentClassName={styles.filterAccordionContent}
                    defaultOpen={true}
                >
                    {Object.entries(allDepartmentsAndServices).map(
                        ([key, department], idx) => (
                            <FormCheckbox
                                label={department.label}
                                checked={(
                                    searchParams.search_departments || []
                                ).includes(key.toLowerCase())}
                                onChange={() => {
                                    const lower_cred = key.toLowerCase();
                                    const updatedSearchCreds = [
                                        ...(searchParams.search_departments || []),
                                    ];
                                    if (updatedSearchCreds.includes(lower_cred)) {
                                        setSearchParams({
                                            ...searchParams,
                                            search_departments:
                                                updatedSearchCreds.filter(
                                                    (l) => l !== lower_cred
                                                ),
                                        });
                                    } else {
                                        setSearchParams({
                                            ...searchParams,
                                            search_departments: [
                                                ...(updatedSearchCreds || []),
                                                lower_cred,
                                            ],
                                        });
                                    }
                                }}
                                noBorder
                                key={`check-department-${key}-${idx}`}
                                labelClass={styles.filterAccordionLabel}
                            />
                        )
                    )}
                </Accordion>

                {/* Age Accordion */}
                <Accordion
                    title="Age"
                    variant="bordered"
                    className={styles.filterAccordion}
                    titleClassName={styles.filterAccordionTitle}
                    contentClassName={styles.filterAccordionContent}
                    defaultOpen={true}
                >
                    {Object.keys(AgeCategory).map((k: string, idx) => (
                        <FormCheckbox
                            label={(AgeCategory as any)[k]}
                            checked={searchParams.age_categories.includes(
                                (AgeCategory as any)[k]
                            )}
                            onChange={(e: any) => {
                                let age_categories = [
                                    ...searchParams.age_categories,
                                ];
                                if (
                                    age_categories.includes((AgeCategory as any)[k])
                                ) {
                                    age_categories = age_categories.filter(
                                        (a) => a !== (AgeCategory as any)[k]
                                    );
                                } else {
                                    age_categories.push((AgeCategory as any)[k]);
                                }
                                updateSearchParams("age_categories", age_categories);
                            }}
                            noBorder
                            key={`check-age-${k}-${idx}`}
                            labelClass={styles.filterAccordionLabel}
                        />
                    ))}
                </Accordion>

                {/* Experience Levels Accordions */}
                {Object.keys(groupedLevels)
                    .filter((k) => k !== "Provider Style")
                    .sort((a, b) => a.localeCompare(b))
                    .map((k, idx) => (
                        <Accordion
                            key={`experience-${k}-${idx}`}
                            title={k}
                            variant="bordered"
                            className={styles.filterAccordion}
                            titleClassName={styles.filterAccordionTitle}
                            contentClassName={styles.filterAccordionContent}
                            defaultOpen={true}
                        >
                            {groupedLevels[k]}
                        </Accordion>
                    ))}

                {methodologyOptions && methodologyOptions.length > 0 && (
                    <Accordion
                        title="Intake - Other"
                        variant="bordered"
                        className={styles.filterAccordion}
                        titleClassName={styles.filterAccordionTitle}
                        contentClassName={styles.filterAccordionContent}
                        defaultOpen={true}
                    >
                        {methodologyOptions.map((value: string, idx: number) => {
                            const checked = Boolean(searchParams.methodology[value]);
                            return (
                                <FormCheckbox
                                    label={value}
                                    checked={checked}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        updateSearchParams("methodology", {
                                            ...searchParams.methodology,
                                            [value]: !checked,
                                        });
                                    }}
                                    noBorder
                                    key={`check-personality-${value}-${idx}`}
                                    labelClass={styles.filterAccordionLabel}
                                />
                            );
                        })}
                    </Accordion>
                )}
            </div>
        </div>
    );
}
